import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { Pencil } from 'lucide-react';
import { hideEditIncidentTypeModal } from '@@/features/modal';
import { usePatchIncidentMutation } from '@@/services/incident';
import { incidentTypes } from '@@/constants/incident';
import { RootState } from '@@/store';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import SelectInput from '@@/components/Inputs/SelectInput';

const schema = yup
  .object({
    type: yup.string().required('Sélectionnez un type'),
  })
  .required();

const EditIncidentTypeModal: React.FC<{
  incidentId: string;
  currentType: string;
}> = ({ incidentId, currentType }) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(
    (state: RootState) => state.modal.showEditIncidentTypeModal,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ type: string }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: { type: currentType },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ type: currentType });
    }
  }, [isOpen, currentType, reset]);

  const closeModal = useCallback(() => {
    dispatch(hideEditIncidentTypeModal());
    reset();
  }, [dispatch, reset]);

  const [patchIncident, { isSuccess }] = usePatchIncidentMutation();

  const onSubmit = useCallback(
    (data: { type: string }) => {
      if (incidentId) {
        patchIncident({
          incidentId,
          type: data.type,
        });
      }
    },
    [patchIncident, incidentId],
  );

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      toast.success("Le type de l'incident a bien été modifié", {
        position: 'top-right',
      });
    }
  }, [isSuccess, closeModal]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Modifier le type d'incident"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <Pencil size={24} className="text-primary-600" />
        <h2 className="text-heading-md text-neutral-900">Modifier le type</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form className="space-y-6">
        <SelectInput
          register={register}
          id="type"
          label="Type d'incident *"
          options={incidentTypes.map((t) => ({
            id: t.id,
            label: t.label,
          }))}
          error={errors.type}
        />
      </form>

      <div className="flex justify-end mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button bg-primary-600 hover:bg-primary-700 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
        >
          Valider
        </button>
      </div>
    </Modal>
  );
};

export default EditIncidentTypeModal;
